<template>
  <v-card data-testid="tract-type-form">
    <v-card-title class="primary white--text">
      <span class="headline mr-2">{{ title }}</span>
      <Icon
      v-if="propTractType && specialization.icon"
      :icon="specialization.icon"
      :tooltipText="specialization.name"
      iconColor="white"
      :small="false"/>
      <v-spacer/>
      <Icon
      @icon-clicked="$emit('close')"
      :tooltipText="$t('close')"
      dataTestId="tract-type-close-button"
      iconColor="white"
      icon="mdi-close"
      :small="false"
      :large="true"
      />
    </v-card-title>
    <v-card-text>
      <FormWrapper
        ref="form"
        formRef="tractTypeForm"
        testId="save-tract-type"
        :lazyValidation="true"
        :buttonText="saveBtnText"
        @submit="saveTractType"
      >
        <v-container fluid>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="tractType.name"
                data-testid="tract-type-name"
                :rules="[rules.required]"
                ref="firstField"
                :label="$t('name')"
                color="black"
                counter="64"
                maxlength="64"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="tractType.code"
                data-testid="tract-type-code"
                :label="$t('code')"
                color="black"
                counter
                maxlength="10"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="tractType.category"
                :rules="[rules.required]"
                item-color="primary"
                :items="TractTypeCategory.enums"
                data-testid="tract-type-category"
                :label="$t('category')"
                color="black"
                return-object
                item-text="name"
                :disabled="propTractType && specialization !== TractTypeSpecialization.None"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" md="8">
              <v-autocomplete
                v-model="createFields"
                :items="createFieldItems"
                :label="$t('requiredAtTractCreation')"
                :hint="$t('requiredAtTractCreationHint')"
                persistent-hint
                data-testid="tract-type-create-fields"
                color="black"
                item-color="primary"
                item-text="name"
                item-value="value"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-autocomplete
                v-model="addContractFields"
                :items="addContractFieldItems"
                :label="$t('requiredToAddContract')"
                :hint="$t('requiredToAddContractHint')"
                persistent-hint
                data-testid="tract-type-add-contract-fields"
                color="black"
                item-color="primary"
                item-text="name"
                item-value="value"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-autocomplete
                v-model="allowedTemplateBundleIds"
                :items="allTemplateBundles"
                :loading="templatesLoading"
                :label="$t('allowedTemplateBundles')"
                :hint="$t('allowedTemplateBundlesHint')"
                persistent-hint
                data-testid="tract-type-template-bundles"
                color="black"
                item-color="primary"
                item-text="name"
                item-value="templateBundleId"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
              />
            </v-col>
          </v-row>
          <v-row v-if="showEntitySelector" dense>
            <v-col cols="12" md="8">
              <EntitySelector
                :entities="businessEntities"
                :initialEntity="selectedEntity"
                :hint="$t('selectBusinessEntityForTractTypeChange')"
                selectStyle="width: 100%;"
                @entity-selected="entitySelected"
                persistentHint
              />
            </v-col>
          </v-row>
        </v-container>
        <template #right-action v-if="!propTractType">
          <v-autocomplete
          style="min-width: 200px;"
          :disabled="tractType.category.value === TractTypeCategory.Delivered.value"
          class="mr-2"
          :label="$t('specialization')"
          color="primary"
          v-model="tractType.specialization"
          item-text="name"
          item-value="value"
          :items="TractTypeSpecialization.enums">
            <template v-slot:item="{ item }">
              <Icon v-if="item.icon" :icon="item.icon" class="mr-2" :small="false"/>
              <span>{{ item.name }}</span>
            </template>
            <template v-slot:selection="{ item }">
              <Icon v-if="item.icon" :icon="item.icon" class="mr-2" :small="false"/>
              <span>{{ item.name }}</span>
            </template>
          </v-autocomplete>
        </template>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import { rules } from '@/utils/FormRules.js'
import { TractTypeCategory, TractTypeSpecialization, TractRequirementType, TractRequiredField } from '@/utils/Enumerations.js'
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'TractTypeForm',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    EntitySelector: () => import('@/components/accounting/EntitySelector.vue')
  },

  props: {
    propTractType: {
      type: Object,
      default: undefined
    }
  },

  data: () => ({
    TractTypeSpecialization,
    rules,
    tractType: {
      code: '',
      name: '',
      category: undefined,
      specialization: TractTypeSpecialization.None.value
    },
    createFields: [],
    addContractFields: [],
    allowedTemplateBundleIds: [],
    selectedEntity: undefined,
    TractTypeCategory
  }),

  async created () {
    if (this.allTemplateBundles.length === 0) this.fetchTemplateBundles()
    if (this.propTractType) {
      await this.fetchAllBusinessEntities()
      this.selectedEntity = this.businessEntities[0]
      this.tractType = JSON.parse(JSON.stringify(this.propTractType))
      this.tractType.category = TractTypeCategory.forInt(this.tractType.category)
      const rules = this.tractType.requiredFields ?? []
      this.createFields = rules.filter(rule => rule.requirementType === TractRequirementType.Create.value).map(rule => rule.field)
      this.addContractFields = rules.filter(rule => rule.requirementType === TractRequirementType.AddContract.value).map(rule => rule.field)
      this.allowedTemplateBundleIds = (this.tractType.templateBundles ?? []).map(bundle => bundle.templateBundleId)
    } else {
      this.tractType.category = TractTypeCategory.forInt(1)
    }
  },

  mounted () {
    setTimeout(_ => {
      this.$refs.firstField.focus()
    }, 0)
  },

  computed: {
    ...mapGetters('user', ['businessEntities']),
    ...mapGetters('activity-templates', ['allTemplateBundles', 'templatesLoading']),
    title () {
      return this.propTractType ? this.$t('editingTractType') : this.$t('creatingTractType')
    },

    saveBtnText () {
      return this.propTractType ? this.$t('saveChanges') : this.$t('createNewTractType')
    },

    changedToStumpage () {
      return this.propTractType?.category === TractTypeCategory.Delivered.value && this.tractType.category?.value === TractTypeCategory.Stumpage.value
    },

    // The server assigns the only entity itself on single-entity tenants.
    showEntitySelector () {
      return this.changedToStumpage && this.businessEntities.length > 1
    },

    specialization () {
      return TractTypeSpecialization.forInt(this.tractType.specialization)
    },

    createFieldItems () {
      return this.fieldItemsFor(TractRequirementType.Create).filter(field => !this.addContractFields.includes(field.value))
    },

    addContractFieldItems () {
      return this.fieldItemsFor(TractRequirementType.AddContract).filter(field => !this.createFields.includes(field.value))
    }
  },

  watch: {
    tractType: {
      handler () {
        if (this.tractType.category?.value === TractTypeCategory.Delivered.value) {
          this.tractType.specialization = TractTypeSpecialization.None.value
        }
      },
      deep: true
    },

    createFieldItems (items) {
      this.createFields = this.pruneUnavailable(this.createFields, items)
    },

    addContractFieldItems (items) {
      this.addContractFields = this.pruneUnavailable(this.addContractFields, items)
    }
  },

  methods: {
    ...mapActions('tract-type', ['createTractType', 'updateTractType']),
    ...mapActions('user', ['fetchAllBusinessEntities']),
    ...mapActions('activity-templates', ['fetchTemplateBundles']),

    // Returns the same array when nothing was pruned: a fresh reference would re-trigger the other
    // stage's items and loop the two watchers forever.
    pruneUnavailable (selected, items) {
      const allowed = items.map(i => i.value)
      return selected.some(field => !allowed.includes(field)) ? selected.filter(field => allowed.includes(field)) : selected
    },

    fieldItemsFor (requirementType) {
      const category = this.tractType.category?.value
      return TractRequiredField.enums
        .filter(field =>
          field.details.requirementTypes.includes(requirementType.value) &&
          field.details.categories.includes(category) &&
          field.details.specializations.includes(this.tractType.specialization))
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    async saveTractType () {
      if (!this.$refs.form.$refs.tractTypeForm.validate()) {
        return
      }

      const requestObj = JSON.parse(JSON.stringify(this.tractType))
      requestObj.category = requestObj.category.value
      requestObj.requiredFields = [
        ...this.createFields.map(field => ({ requirementType: TractRequirementType.Create.value, field })),
        ...this.addContractFields.map(field => ({ requirementType: TractRequirementType.AddContract.value, field }))
      ]
      requestObj.templateBundleIds = [...this.allowedTemplateBundleIds]
      if (this.showEntitySelector) requestObj.businessEntityId = this.selectedEntity?.businessEntityId

      if (this.propTractType) {
        await this.updateTractType(requestObj)
        this.$emit('tract-type-mutated')
      } else {
        const response = await this.createTractType(requestObj)
        this.$emit('tract-type-mutated', response)
      }
    },
    entitySelected (entity) {
      this.selectedEntity = entity
    }
  }
}
</script>
